import type { CSSProperties } from "react";
import { pathFor } from "../../../app/router/routes";
import { HOME_ART, HOME_BACKDROP } from "../assets";
import type { MarketingCopy } from "../copy";
import { LockIcon, TickIcon, VoiceIcon } from "../icons";
import { BackdropVideo } from "./BackdropVideo";
import { CtaLink } from "./Cta";

export interface HeroProps {
  t: MarketingCopy;
}

/**
 * The home page's first screen: the world the games live in, moving.
 *
 * 🔴 TWO ACTIONS, AND THEY ARE FOR TWO DIFFERENT READERS. "Book a school demo"
 * is the conversion — the school buyer this page is for. "Play Sky Quest free"
 * is the proof — the one game a stranger can open right now, with no account.
 * The old hero's single "Get Started" sent both of them to the door picker,
 * which is a sign-in screen for an account neither of them has.
 *
 * ⚠️ THE BUBBLE AND THE CAMERA CARD ARE ILLUSTRATIONS, NOT LIVE UI. They show,
 * at a glance, the two things the product does that a reader cannot guess —
 * prompts are spoken aloud, and the camera reads a pose without recording it.
 * Both are `aria-hidden`; the facts they illustrate are in the trust strip
 * below, as text a screen reader gets.
 */
export function Hero({ t }: HeroProps) {
  return (
    <section className="relative isolate overflow-hidden bg-sky">
      <BackdropVideo
        backdrop={HOME_BACKDROP}
        pauseLabel={t.video.pause}
        playLabel={t.video.play}
        objectPosition="center 70%"
        controlClassName="absolute right-4 bottom-24 sm:right-6 lg:top-32 lg:right-10 lg:bottom-auto xl:right-[max(2.5rem,calc((100vw_-_1240px)/2_+_2.5rem))]"
      />
      <div aria-hidden="true" className="kq-hero-shade pointer-events-none absolute inset-0" />

      <div className="relative z-10 mx-auto grid w-full max-w-[1240px] gap-6 px-5 pt-28 pb-24 kq-hero-frame sm:px-6 sm:pt-32 lg:grid-cols-[1.05fr_1fr] lg:items-center lg:gap-6 lg:px-10 lg:pt-28 lg:pb-28">
        <div className="kq-on-photo text-surface">
          <p
            className="kq-rise-in inline-flex items-center gap-2.5 rounded-pill border border-surface/55 bg-surface/20 py-1.5 pr-4 pl-2.5 font-dash text-dash-xs font-extrabold backdrop-blur-md"
          >
            <span aria-hidden="true" className="kq-pulse h-2.5 w-2.5 rounded-pill bg-leaf" />
            {t.hero.eyebrow}
          </p>

          <h1 className="kq-hero mt-5 font-kid font-extrabold">
            <span className="kq-rise-in block" style={{ "--kq-delay": "80ms" } as CSSProperties}>
              {t.hero.headlineMove}
            </span>
            <span className="kq-rise-in block" style={{ "--kq-delay": "160ms" } as CSSProperties}>
              {t.hero.headlinePlay}
            </span>
            <span className="kq-rise-in block" style={{ "--kq-delay": "240ms" } as CSSProperties}>
              <span className="kq-sticker">{t.hero.headlineLearn}</span>
            </span>
          </h1>

          <p
            className="kq-rise-in mt-6 max-w-[38ch] font-dash text-dash-lg font-bold text-surface/95"
            style={{ "--kq-delay": "320ms" } as CSSProperties}
          >
            {t.hero.subline}
          </p>

          <div
            className="kq-rise-in mt-8 flex flex-col gap-4 sm:flex-row sm:items-center"
            style={{ "--kq-delay": "400ms" } as CSSProperties}
          >
            <CtaLink href={pathFor("contact-sales")} variant="primary" data-testid="book-demo-hero">
              {t.cta.bookSchoolDemo}
            </CtaLink>
            <CtaLink href={pathFor("sky-quest-play")} variant="glass" icon="play">
              {t.cta.playSkyQuestFree}
            </CtaLink>
          </div>

          <ul
            className="kq-rise-in mt-7 flex flex-wrap gap-x-5 gap-y-2"
            style={{ "--kq-delay": "480ms" } as CSSProperties}
          >
            {t.hero.proof.map((line) => (
              <li key={line} className="inline-flex items-center gap-2 font-dash text-dash-xs font-extrabold">
                <span aria-hidden="true" className="flex h-5 w-5 items-center justify-center rounded-pill bg-surface/25">
                  <TickIcon className="h-3.5 w-3.5" />
                </span>
                {line}
              </li>
            ))}
          </ul>
        </div>

        {/* ── The cast, standing on the meadow ─────────────────────────────── */}
        <div className="relative mx-auto w-full max-w-[560px] lg:max-w-none lg:self-end">
          <img
            src={HOME_ART.cast}
            alt={t.hero.castAlt}
            width={960}
            height={853}
            decoding="async"
            className="kq-float-sm relative z-10 mx-auto w-full max-w-[600px] object-contain drop-shadow-[0_24px_30px_color-mix(in_srgb,var(--color-ink)_30%,transparent)]"
          />

          {/* Spoken-prompt bubble */}
          <div
            aria-hidden="true"
            className="kq-bob absolute top-[6%] left-[-4%] z-20 hidden items-center gap-3 rounded-xl bg-surface py-3 pr-5 pl-3 shadow-[0_8px_0_color-mix(in_srgb,var(--color-ink)_8%,transparent),var(--shadow-raised)] sm:flex lg:top-[2%] lg:left-[-10%]"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-pill bg-primary text-primary-ink">
              <VoiceIcon className="h-5 w-5" />
            </span>
            <span>
              <span className="block font-dash text-dash-xs font-bold text-muted">{t.hero.promptLabel}</span>
              <span className="block font-kid text-dash-lg leading-tight font-extrabold text-ink">{t.hero.prompt}</span>
            </span>
            <span className="absolute -bottom-2.5 left-14 h-5 w-5 rotate-45 rounded-sm bg-surface" />
          </div>

          {/* Camera card — illustrative */}
          <div
            aria-hidden="true"
            className="kq-float absolute top-[-8%] right-[-2%] z-20 hidden w-52 rotate-3 lg:block"
            style={{ "--kq-delay": "1.2s" } as CSSProperties}
          >
            <div className="relative h-36 overflow-hidden rounded-xl border-4 border-surface bg-ink shadow-raised">
              <span className="absolute top-2 left-2 z-10 inline-flex items-center gap-1.5 rounded-pill bg-leaf px-2.5 py-1 font-dash text-dash-xs font-black text-surface">
                <span className="h-1.5 w-1.5 rounded-pill bg-surface" />
                {t.hero.cameraChip}
              </span>
              <span className="absolute top-9 left-1/2 h-24 w-20 -translate-x-1/2 rounded-lg border-2 border-dashed border-leaf" />
              <span className="absolute top-12 left-1/2 h-8 w-8 -translate-x-1/2 rounded-pill bg-line-strong/70" />
              <span className="absolute top-[5.5rem] left-1/2 h-16 w-16 -translate-x-1/2 rounded-t-pill bg-line-strong/70" />
            </div>
            <p className="mt-2 inline-flex items-center gap-2 rounded-lg bg-surface/95 px-3 py-1.5 font-dash text-dash-xs font-extrabold text-ink-soft shadow-card">
              <LockIcon className="h-3.5 w-3.5 text-teal" />
              {t.hero.cameraCaption}
            </p>
          </div>
        </div>
      </div>

      {/* The page's white rises out of the meadow instead of meeting it at a ruler-straight edge. */}
      <svg
        aria-hidden="true"
        viewBox="0 0 1440 96"
        preserveAspectRatio="none"
        className="absolute inset-x-0 -bottom-px z-10 h-12 w-full text-surface sm:h-20"
      >
        <path
          fill="currentColor"
          d="M0 58C180 26 360 18 560 40C760 62 920 86 1120 70C1260 59 1360 40 1440 30V96H0Z"
        />
      </svg>
    </section>
  );
}
